// Libs
import classNames from 'classnames/bind';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Badge, Box, ListItemAvatar, ListItemButton, ListItemText, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseAvatar, TypingDots } from '~/components/common';
// Others
import { varColor } from '~/themes/themeVariables';
import { MessageStatusEnum, MessageTypeEnum } from '~/utils/enum/common';
import { IConversation } from '~/utils/interface/conversation';
import { getFullName } from '~/utils/helper/common';
import { DEFAULT_LIMIT_COUNT_MESSAGE, EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './ConversationItem.module.scss';
import { icons } from '~/assets';

type Props<T> = {
  selected?: boolean;
  conversation?: T;
  isTyping?: boolean;
  onClick?: (conversation: T) => void;
};

const cx = classNames.bind(styles);

const ConversationItem = <T extends IConversation>(props: Props<T>) => {
  //#region Destructuring Props
  const { selected = false, conversation, isTyping = false, onClick } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/conversation', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const renderLastMessageContent = () => {
    if (isTyping) {
      return (
        <Box component={'div'} className={cx('typingDots')}>
          <TypingDots height={22} />
        </Box>
      );
    }

    switch (conversation?.lastMessage?.type) {
      case MessageTypeEnum.FILE:
        return (
          <Stack direction={'row'} alignItems={'center'} gap={'8px'}>
            <img
              src={icons.conversationIconFileDefault}
              alt={t('common_alt_img', { ns: 'common/base' })}
              className={cx('lastMessageIcon')}
            />

            <Typography component={'p'} className={cx('conversationItemMessage')}>
              {t('conversation_sent_file')}
            </Typography>
          </Stack>
        );

      default:
        return conversation?.lastMessage?.content || EMPTY_STRING;
    }
  };

  const renderMessageStatus = () => {
    switch (conversation?.lastMessage?.status) {
      case MessageStatusEnum.SENT:
        return (
          <img src={icons.conversationIconStatusSent} alt={t('common_alt_img', { ns: 'common/base' })} className='' />
        );

      case MessageStatusEnum.DELIVERED:
        return (
          <img
            src={icons.conversationIconStatusDelivered}
            alt={t('common_alt_img', { ns: 'common/base' })}
            className=''
          />
        );

      case MessageStatusEnum.SEEN:
        return (
          <img src={icons.conversationIconStatusSeen} alt={t('common_alt_img', { ns: 'common/base' })} className='' />
        );

      default:
        return EMPTY_STRING;
    }
  };

  const handleConversationClick = () => {
    if (!conversation) return;

    onClick && onClick(conversation);
  };
  //#endregion Handle Function

  return (
    <Box component={'li'} className={cx('container')}>
      <ListItemButton
        alignItems='center'
        className={cx('listItem')}
        selected={selected}
        onClick={handleConversationClick}
        sx={{ border: 'none' }}
      >
        <ListItemAvatar className={cx('listItemAvatar')}>
          <Badge
            overlap='circular'
            color='default'
            variant='dot'
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: conversation?.account?.isOnline ? varColor.statusOnline : varColor.statusOffline,
              },
            }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{
              badge: {
                className: cx('badge'),
              },
            }}
          >
            <BaseAvatar
              dataAccount={{
                avatarImage: conversation?.account?.avatarImage,
                firstName: conversation?.account?.firstName,
                lastName: conversation?.account?.lastName,
              }}
            />
          </Badge>
        </ListItemAvatar>

        <ListItemText
          className={cx('listItemGroup')}
          primary={
            <Typography component={'span'} className={cx('conversationItemName')}>
              {getFullName({ ...conversation?.account })}
            </Typography>
          }
          {...(conversation?.lastMessage && {
            secondary: (
              <Typography
                component='span'
                variant='body2'
                className={cx(
                  conversation?.countUnseen > 0 && !isTyping ? 'conversationItemName' : 'conversationItemMessage'
                )}
              >
                {renderLastMessageContent()}
              </Typography>
            ),
          })}
        />

        <Box component={'div'} className={cx('conversationFooter')}>
          <Typography component={'span'} className={cx('dateTime')}>
            {moment(conversation?.lastMessage?.createdAt).fromNow()}
          </Typography>

          <Box
            component={'div'}
            className={cx('status', { noMessage: !conversation?.countUnseen && !conversation?.lastMessage })}
          >
            {conversation?.countUnseen && conversation?.countUnseen > 0 ? (
              <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} className={cx('countMessage')}>
                <Typography component={'p'} className={cx('countMessageContent')}>
                  {conversation?.countUnseen > DEFAULT_LIMIT_COUNT_MESSAGE
                    ? `${DEFAULT_LIMIT_COUNT_MESSAGE}+`
                    : conversation?.countUnseen}
                </Typography>
              </Stack>
            ) : (
              renderMessageStatus()
            )}
          </Box>
        </Box>
      </ListItemButton>
    </Box>
  );
};

export default ConversationItem;
